import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { FaCoins, FaSpinner, FaSearch, FaStar } from 'react-icons/fa';
import { BANNED_SYMBOLS, BANNED_NAMES } from '../common/constants';

const SelectorContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Label = styled.div`
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.fontSize.md};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.theme.spacing.xl};
  
  svg {
    animation: spin 1s linear infinite;
    color: ${props => props.theme.colors.accent};
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const CoinList = styled.div`
  margin-top: ${props => props.theme.spacing.md};
  max-height: 450px;
  overflow-y: auto;
  overflow-x: hidden; /* prevent horizontal scroll on hover scaling */
  display: grid;
  gap: ${props => props.theme.spacing.sm};
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background.secondary};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 3px;
    
    &:hover {
      background: ${props => props.theme.colors.primary};
    }
  }
`;

const CoinItem = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: ${props => props.theme.spacing.xs};
  align-items: center;
  padding: 8px 12px;
  width: 100%;
  background: linear-gradient(135deg, 
    ${props => props.theme.colors.background.card} 0%, 
    ${props => props.theme.colors.background.secondary} 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: ${props => props.theme.borderRadius.sm};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  min-height: 44px;
  backdrop-filter: blur(10px);
  will-change: transform; /* smoother transform without layout shift */
  transform-origin: center; /* avoid growing outwards causing x-overflow */
  contain: paint; /* confine painting effects like shadows */
  
  /* Subtle inner glow */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(135deg, 
      rgba(255, 255, 255, 0.1) 0%, 
      rgba(255, 255, 255, 0.05) 50%,
      transparent 100%);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-2px) scale(1.005);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.12),
      0 2px 8px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
    border-color: ${props => props.theme.colors.primary}40;
    background: linear-gradient(135deg, 
      ${props => props.theme.colors.background.cardHover || props.theme.colors.background.card} 0%, 
      ${props => props.theme.colors.background.secondary} 100%);
  }
  
  &.selected {
    background: linear-gradient(135deg, 
      ${props => props.theme.colors.primaryAlpha(0.15)} 0%, 
      ${props => props.theme.colors.primaryAlpha(0.08)} 100%);
    border-color: ${props => props.theme.colors.primary}60;
    box-shadow: 
      0 0 0 1px ${props => props.theme.colors.primary}40,
      0 4px 20px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }
`;

const CoinIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
  
  /* Fallback for coins without icons */
  &.fallback {
    background: ${props => getCoinGradient(props.symbol)};
    color: white;
    font-weight: 800;
    font-size: 18px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border: 2px solid rgba(255, 255, 255, 0.2);
  }
  
  /* Ring effect */
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border-radius: 50%;
    background: linear-gradient(45deg, 
      ${props => props.theme.colors.primary}40 0%, 
      transparent 50%, 
      ${props => props.theme.colors.secondary}40 100%);
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

// Function to get unique gradient for each coin
const getCoinGradient = (symbol) => {
  const gradients = {
    'BTC': 'linear-gradient(135deg, #f7931a 0%, #ff6b35 100%)',
    'ETH': 'linear-gradient(135deg, #627eea 0%, #4a90e2 100%)',
    'BNB': 'linear-gradient(135deg, #f3ba2f 0%, #ffd700 100%)',
    'ADA': 'linear-gradient(135deg, #0033ad 0%, #3967d6 100%)',
    'SOL': 'linear-gradient(135deg, #9945ff 0%, #14f195 100%)',
    'DOT': 'linear-gradient(135deg, #e6007a 0%, #ff4081 100%)',
    'CAKE': 'linear-gradient(135deg, #d1884f 0%, #ffab40 100%)',
    'USDT': 'linear-gradient(135deg, #26a17b 0%, #50e3c2 100%)',
    'USDC': 'linear-gradient(135deg, #2775ca 0%, #4fc3f7 100%)',
    'XRP': 'linear-gradient(135deg, #23292f 0%, #546e7a 100%)',
    'GTAI': 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  };
  
  return gradients[symbol?.toUpperCase()] || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
};

// Safe crypto icon component with multiple CDN fallbacks
const SafeCryptoIcon = ({ symbol, name }) => {
  const [currentCdnIndex, setCurrentCdnIndex] = React.useState(0);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  
  // Multiple CDN sources without CORS issues
  const getCdnSources = (symbol) => {
    const sym = symbol?.toLowerCase();
    return [
      `https://assets.coincap.io/assets/icons/${sym}@2x.png`,
      `https://s2.coinmarketcap.com/static/img/coins/64x64/${getCoinMarketCapId(sym)}.png`,
      `https://cryptoicon-api.vercel.app/api/icon/${sym}`,
    ];
  };
  
  // Map symbols to CoinMarketCap IDs for fallback
  const getCoinMarketCapId = (symbol) => {
    const idMap = {
      'btc': '1', 'eth': '1027', 'bnb': '1839', 'ada': '2010',
      'sol': '5426', 'dot': '6636', 'usdt': '825', 'usdc': '3408',
      'xrp': '52', 'matic': '3890', 'avax': '5805', 'link': '1975',
      'gtai': '16711'
    };
    return idMap[symbol] || '1'; // fallback to BTC
  };
  
  const handleImageError = () => {
    const sources = getCdnSources(symbol);
    if (currentCdnIndex < sources.length - 1) {
      setCurrentCdnIndex(currentCdnIndex + 1);
      setImageLoaded(false);
    } else {
      // All CDNs failed, show fallback
      setImageLoaded(false);
    }
  };
  
  const handleImageLoad = () => {
    setImageLoaded(true);
  };
  
  if (!symbol) {
    return (
      <CoinIcon className="fallback" symbol="?">
        ?
      </CoinIcon>
    );
  }
  
  const sources = getCdnSources(symbol);
  const showFallback = currentCdnIndex >= sources.length;
  
  if (showFallback) {
    return (
      <CoinIcon className="fallback" symbol={symbol}>
        {symbol.charAt(0)}
      </CoinIcon>
    );
  }
  
  return (
    <CoinIcon symbol={symbol}>
      <img 
        src={sources[currentCdnIndex]}
        alt={name || symbol}
        onError={handleImageError}
        onLoad={handleImageLoad}
        style={{ 
          opacity: imageLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease'
        }}
      />
      {!imageLoaded && (
        <div style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: getCoinGradient(symbol),
          color: 'white',
          fontWeight: 800,
          fontSize: '14px',
          borderRadius: '50%'
        }}>
          {symbol.charAt(0)}
        </div>
      )}
    </CoinIcon>
  );
};



const CoinInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const CoinName = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSize.xs};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`;

const CoinSymbol = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.text.muted};
  font-weight: 500;
  line-height: 1.1;
`;

const CoinMetrics = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  min-width: 100px;
`;

const CoinPrice = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSize.md};
  text-align: right;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  line-height: 1.2;
`;

const CoinVolume = styled.div`
  font-size: ${props => props.theme.fontSize.xs};
  color: ${props => props.theme.colors.text.muted};
  text-align: right;
  font-weight: 500;
  opacity: 0.7;
  line-height: 1.1;
`;

const PriceChange = styled.div`
  font-size: ${props => props.theme.fontSize.xs};
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  min-width: 50px;
  text-align: center;
  color: ${props => props.color || props.theme.colors.text.secondary};
  background: ${props => {
    if (props.color === '#10B981') return 'rgba(16, 185, 129, 0.15)';
    if (props.color === '#EF4444') return 'rgba(239, 68, 68, 0.15)';
    return 'rgba(107, 114, 128, 0.15)';
  }};
  border: 1px solid ${props => {
    if (props.color === '#10B981') return 'rgba(16, 185, 129, 0.3)';
    if (props.color === '#EF4444') return 'rgba(239, 68, 68, 0.3)';
    return 'rgba(107, 114, 128, 0.3)';
  }};
  transition: all 0.2s ease;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  backdrop-filter: blur(10px);
  line-height: 1.1;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  }
`;



const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const PopularCoins = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
  align-items: center;
  padding: ${props => props.theme.spacing.sm};
  background: rgba(255, 255, 255, 0.03);
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const PopularCoinChip = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${props => props.theme.borderRadius.md};
  background: rgba(255, 255, 255, 0.05);
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.fontSize.xs};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
  justify-content: center;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &.selected {
    background: ${props => props.theme.colors.primary};
    color: white;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  /* Smaller icon for popular coins */
  ${CoinIcon} {
    width: 20px;
    height: 20px;
    font-size: 10px;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.sm} ${props => props.theme.spacing.sm} 40px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background.card};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSize.sm};
  transition: border-color ${props => props.theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder {
    color: ${props => props.theme.colors.text.muted};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.text.muted};
  pointer-events: none;
`;



const NoCoinsMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.text.muted};
  font-style: italic;
`;

// Helper function for price change display
const getPriceChangeDisplay = (coin, format = 'short') => {
  const priceChange = coin.priceChange24h !== undefined && coin.priceChange24h !== null 
    ? coin.priceChange24h 
    : coin.price_change_24h !== undefined && coin.price_change_24h !== null
    ? coin.price_change_24h
    : null;
  
  if (priceChange !== null && !isNaN(priceChange) && Math.abs(priceChange) > 0.001) {
    const isPositive = priceChange > 0;
    const formattedValue = format === 'short' ? priceChange.toFixed(1) : priceChange.toFixed(2);
    const arrow = format === 'short' ? '' : (isPositive ? '↗' : '↘');
    
    return {
      value: `${isPositive ? '+' : ''}${formattedValue}%`,
      isPositive,
      arrow
    };
  }
  return null;
};

const CoinSelector = ({ coins = [], loading = false, selectedCoin = null, onCoinSelect, tokenPrices = {}, tokenVolumes = {}, selectedExchange = null }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  // Simple safety check and filter out banned coins
  const safeCoins = useMemo(() => {
    if (!Array.isArray(coins)) return [];
    
    return coins.filter(coin => {
      if (!coin || typeof coin !== 'object') return false;
      
      // Ban by symbol
      if (BANNED_SYMBOLS.has(coin.symbol?.toUpperCase())) return false;
      
      // Ban by name
      if (BANNED_NAMES.has(coin.name)) return false;
      
      // Ban extremely low prices (likely shitcoins)
      if (coin.price && coin.price < 0.0001) return false;
      
      return true;
    });
  }, [coins]);
  
  // Filter coins based on search query
  const filteredCoins = useMemo(() => {
    if (!searchQuery.trim()) return safeCoins;
    
    const query = searchQuery.toLowerCase();
    return safeCoins.filter(coin => 
      coin.symbol?.toLowerCase().includes(query) ||
      coin.name?.toLowerCase().includes(query) ||
      coin.address?.toLowerCase().includes(query)
    );
  }, [safeCoins, searchQuery]);
  
  // Popular coins based on common trading symbols - original working version
  const popularSymbols = useMemo(() => ['BTC', 'ETH', 'BNB', 'GTAI', 'DOT', 'SOL', 'ADA', 'CAKE'], []);
  
  // Get popular coins that are available in the current list
  const availablePopularCoins = useMemo(() => {
    return popularSymbols
      .map(symbol => safeCoins.find(coin => coin && coin.symbol === symbol))
      .filter(Boolean)
      .slice(0, 6); // Limit to 6 popular coins
  }, [safeCoins, popularSymbols]);

  const handleCoinSelect = (coin) => {
    if (onCoinSelect && coin) {
      onCoinSelect(coin);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  if (loading) {
    return (
      <SelectorContainer>
        <Label>
          <FaCoins />
          <span>Select Token</span>
        </Label>
        <LoadingSpinner>
          <FaSpinner size={30} />
        </LoadingSpinner>
      </SelectorContainer>
    );
  }

  return (
    <SelectorContainer>
      <Label>
        <FaCoins />
        <span>Select Token ({filteredCoins.length} available)</span>
      </Label>
      
      <TopRow>
        {availablePopularCoins.length > 0 && !searchQuery && (
          <PopularCoins>
            {availablePopularCoins.map((coin) => (
              <PopularCoinChip
                key={coin.address || coin.symbol}
                className={selectedCoin?.address === coin.address ? 'selected' : ''}
                onClick={() => handleCoinSelect(coin)}
              >
                <FaStar size={10} />
                {coin.symbol}
                {/* Add price change indicator to popular coins */}
                {(() => {
                  const priceChange = coin.priceChange24h !== undefined && coin.priceChange24h !== null 
                    ? coin.priceChange24h 
                    : coin.price_change_24h !== undefined && coin.price_change_24h !== null
                    ? coin.price_change_24h
                    : null;
                  
                  if (priceChange !== null && !isNaN(priceChange) && Math.abs(priceChange) > 0.001) {
                    const isPositive = priceChange > 0;
                    return (
                      <span style={{
                        fontSize: '10px',
                        marginLeft: '4px',
                        color: isPositive ? '#10B981' : '#EF4444',
                        fontWeight: '600'
                      }}>
                        {isPositive ? '+' : ''}{priceChange.toFixed(1)}%
                      </span>
                    );
                  }
                  return null;
                })()}
              </PopularCoinChip>
            ))}
          </PopularCoins>
        )}
        <SearchContainer>
          <SearchIcon>
            <FaSearch size={16} />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search by symbol, name, or address..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </SearchContainer>
      </TopRow>
      
      <CoinList>
        {filteredCoins.length === 0 ? (
          <NoCoinsMessage>
            {searchQuery ? `No tokens found for "${searchQuery}"` : 'No tokens available'}
          </NoCoinsMessage>
        ) : (
          filteredCoins.map((coin, index) => (
            <CoinItem
              key={coin.address || coin.symbol || index}
              className={selectedCoin?.address === coin.address ? 'selected' : ''}
              onClick={() => handleCoinSelect(coin)}
            >
              <SafeCryptoIcon symbol={coin.symbol} name={coin.name} />
              
              <CoinInfo>
                <CoinName>{coin.name || 'Unknown'}</CoinName>
                <CoinSymbol>{coin.symbol || '???'}</CoinSymbol>
              </CoinInfo>
              
              <CoinMetrics>
                <CoinPrice>
                  ${coin.price && typeof coin.price === 'number' ? coin.price.toFixed(8) : '0.00000000'}
                </CoinPrice>
                <CoinVolume>
                  Vol: ${(() => {
                    const volume = coin.volume;
                    if (volume && typeof volume === 'number') {
                      if (volume > 1000000) {
                        return (volume / 1000000).toFixed(1) + 'M';
                      } else if (volume > 1000) {
                        return (volume / 1000).toFixed(1) + 'K';
                      } else {
                        return volume.toFixed(0);
                      }
                    }
                    return '0';
                  })()}
                </CoinVolume>
                {/* Price change display */}
                {(() => {
                  const priceChangeData = getPriceChangeDisplay(coin, 'long');
                  if (priceChangeData) {
                    return (
                      <PriceChange color={priceChangeData.isPositive ? '#10B981' : '#EF4444'}>
                        {priceChangeData.arrow} {priceChangeData.value}
                      </PriceChange>
                    );
                  }
                  
                  return (
                    <PriceChange color="#6B7280">
                      → 0.00%
                    </PriceChange>
                  );
                })()}
              </CoinMetrics>
            </CoinItem>
          ))
        )}
      </CoinList>
    </SelectorContainer>
  );
};

export { SafeCryptoIcon };
export default React.memo(CoinSelector);